import { Conversation } from "..";
import LoadingSpinner from "./LoadingSpinner";
import AgentItem from "./AgentItem";
import ConversationItem from "./ConversationItem";
import { Agent } from "types";
import React, { useState, useMemo } from "react";
import Card from "components/card";
import SearchIcon from "components/icons/SearchIcon";
import { ChevronsLeft, ChevronsRight } from "lucide-react";

interface ChatSidebarPropsContent {
  conversations: Conversation[];
  availableAgents: Agent[];
  canContinue: boolean;
  isLoadingConversations: boolean;
  isLoadingAgents: boolean;
  onConversationClick: (conversationId: string, agentId: string) => void;
  onLoadMore: () => void;
  selectedAgentId?: string;
  onAgentSelect: (agentId: string) => void;
  conversationsError: string | null;
  agentsError: string | null;
  onRetryConversations: () => void;
  onRetryAgents: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const ChatSidebarContent: React.FC<ChatSidebarPropsContent> = ({
  agentsError,
  availableAgents,
  canContinue,
  conversations,
  conversationsError,
  isLoadingAgents,
  isLoadingConversations,
  onAgentSelect,
  onConversationClick,
  onLoadMore,
  onRetryAgents,
  onRetryConversations,
  selectedAgentId,
  isCollapsed,
  onToggleCollapse,
}) => {
  // Only one search input for both agents and conversations
  const [search, setSearch] = useState("");
  const [showAllAgents, setShowAllAgents] = useState(false);

  const filteredAgents = useMemo(() => {
    const s = search.toLowerCase();
    return availableAgents.filter((agent) =>
      agent.name?.toLowerCase().includes(s)
    );
  }, [availableAgents, search]);

  const filteredConversations = useMemo(() => {
    const s = search.toLowerCase();
    return conversations.filter((convo) =>
      convo.name?.toLowerCase().includes(s)
    );
  }, [conversations, search]);

  const groupedConversations = useMemo(() => {
    const now = new Date();
    const last7Days: typeof filteredConversations = [];
    const prev30Days: typeof filteredConversations = [];
    const older: typeof filteredConversations = [];
    filteredConversations.forEach((convo) => {
      const updated = new Date(convo.updated_at);
      const diffDays =
        (now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24);

      if (diffDays <= 7) {
        last7Days.push(convo);
      } else if (diffDays <= 37) {
        prev30Days.push(convo);
      } else {
        older.push(convo);
      }
    });
    return [
      { label: "Last 7 days", items: last7Days },
      { label: "Previous 30 days", items: prev30Days },
      { label: "Older", items: older },
    ];
  }, [filteredConversations]);

  const agentsToShow = showAllAgents
    ? filteredAgents
    : filteredAgents.slice(0, 5);
  const hasMoreAgents = filteredAgents.length > 5;

  return (
    <Card extra="h-full">
      {!isCollapsed && (
        <div className="border-b border-[#ececec] px-6 pb-2 dark:border-navy-700">
          <div
            className={`hidden items-center lg:flex ${
              isCollapsed ? "justify-center" : "justify-between"
            }`}
          >
            <p className="text-lg font-semibold text-[#6c6f93] dark:text-white">
              Agent Chat
            </p>
          </div>

          <div className={`  pt-4 ${isCollapsed ? "hidden" : ""}`}>
            <div className="mb-3 flex h-8 w-full items-center rounded-lg border !border-gray-200 px-2 dark:!border-navy-700 dark:!bg-navy-700">
              <SearchIcon />
              <input
                placeholder="Search agents or conversations..."
                className="h-6 !w-full rounded-lg px-1 text-xs font-medium outline-none dark:bg-navy-700 dark:text-white"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
      <button
        onClick={onToggleCollapse}
        className=" absolute -right-3  top-1/2 z-10 hidden size-7 items-center justify-center rounded-full border bg-white text-gray-900 shadow-md hover:bg-gray-100 dark:border-navy-700 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 lg:flex"
      >
        {isCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
      </button>
      {/* Scrollable Content */}
      <div className="flex flex-1 flex-col overflow-auto">
        {/* Agent List */}
        <div
          className={`border-b border-[#ececec] px-6 py-2 dark:border-navy-700 ${
            isCollapsed ? "hidden" : ""
          }`}
        >
          <div className="flex flex-col gap-2">
            {isLoadingAgents ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="small" />
              </div>
            ) : agentsError ? (
              <div className="flex flex-col items-center gap-2 py-4">
                <p className="text-sm text-red-500 dark:text-red-400">
                  {agentsError}
                </p>
                <button
                  onClick={onRetryAgents}
                  className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300"
                >
                  Retry
                </button>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No agents available.
                </p>
              </div>
            ) : (
              <>
                {agentsToShow.map((agent) => (
                  <AgentItem
                    key={agent.id}
                    agent={agent}
                    isSelected={agent.id === selectedAgentId}
                    onClick={onAgentSelect}
                  />
                ))}
                {hasMoreAgents && (
                  <button
                    className="mt-1 self-start text-xs text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300"
                    onClick={() => setShowAllAgents((v) => !v)}
                  >
                    {showAllAgents
                      ? "Show less"
                      : `More (${filteredAgents.length - 5})`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Chat List */}
        <div className={`px-6 pb-2 pt-4 ${isCollapsed ? "hidden" : ""}`}>
          <div className="flex flex-col gap-2">
            {isLoadingConversations ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="small" />
              </div>
            ) : conversationsError ? (
              <div className="flex flex-col items-center gap-2 py-4">
                <p className="text-sm text-red-500 dark:text-red-400">
                  {conversationsError}
                </p>
                <button
                  onClick={onRetryConversations}
                  className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300"
                >
                  Retry
                </button>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No conversations available.
                </p>
              </div>
            ) : (
              <>
                {groupedConversations.map(
                  (group) =>
                    group.items.length > 0 && (
                      <div key={group.label}>
                        <div className="mb-1 mt-2 text-xs font-semibold uppercase text-gray-400 dark:text-gray-500">
                          {group.label}
                        </div>
                        {group.items.map((conversation) => {
                          const agent = availableAgents.find(
                            (a) => a.id === conversation.agent_id
                          );
                          return (
                            <ConversationItem
                              key={conversation.id}
                              conversation={conversation}
                              agent={agent}
                              convoId={conversation.id}
                              onClick={() =>
                                onConversationClick(
                                  conversation.id,
                                  conversation.agent_id
                                )
                              }
                            />
                          );
                        })}
                      </div>
                    )
                )}
                {canContinue && (
                  <button
                    onClick={onLoadMore}
                    className="mt-2 text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300"
                  >
                    Load more conversations
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
